import React from "react";
import "./ClinicalDataManagementServices.css";
import PageHeader from "../../../components/headers/PageHeader";
import wave from "../../../assets/Images/waves/wave_mou.png";

export default function ClinicalDataManagementServices() {
    return (
        <div className="cdm-page">
            <PageHeader
                mediaSrc={wave}
                mediaType="image"
                subtitle="Enhance Clinical Insight"
                title="Clinical Data Management"
            />

            <section className="cdm-section cdm-introSection">
                <h2>Audit-Ready Clinical Data Management</h2>
                <p>
                    Eupraxia delivers comprehensive, audit-ready Clinical Data Management
                    solutions ensuring data integrity, compliance, and traceability across
                    all study phases. Our CDM operations are governed by GxP, CDISC, and
                    21 CFR Part 11 standards, built on validated platforms, and executed
                    through harmonized SOPs.
                </p>
                <p>
                    A key strength of Eupraxia’s model is our ability to work seamlessly
                    with multiple stakeholders and vendors — including EDC providers,
                    laboratories, CRO partners, and statistical teams — ensuring smooth
                    data flow, consistency, and regulatory readiness from first-patient-in
                    to database lock.
                </p>
            </section>

            <section className="cdm-section cdm-cardSection">
                <h2>Our Clinical Data Management Services</h2>

                <p className="cdm-sectionIntro">
                    We offer a complete range of clinical data management services to
                    streamline your processes and maximize the value of your clinical
                    trial data.
                </p>

                <div className="cdm-cardGrid">
                    <article className="cdm-card">
                        <h3>Study Start-Up &amp; Planning</h3>
                        <ul className="cdm-list">
                            <li>Protocol and CRF/eCRF design reviews.</li>
                            <li>Data Management Plan, CRF Completion Guidelines, and Edit Check Specification.</li>
                            <li>Data standards, controlled terminologies, and mapping conventions.</li>
                            <li>Milestone-based build plan and timeline tracking.</li>
                            <li>Configuration management and audit trail implementation.</li>
                        </ul>
                    </article>

                    <article className="cdm-card">
                        <h3>eCRF Design &amp; EDC Configuration</h3>
                        <ul className="cdm-list">
                            <li>CRF design aligned with protocol objectives.</li>
                            <li>EDC configuration across Medidata Rave, Oracle InForm, Veeva, OpenClinica, and sponsor systems.</li>
                            <li>Edit check programming and form validation logic.</li>
                            <li>UAT execution, user training, and go-live documentation.</li>
                            <li>User access and workflow control under 21 CFR Part 11 compliance.</li>
                        </ul>
                    </article>

                    <article className="cdm-card">
                        <h3>Data Validation &amp; Cleaning</h3>
                        <ul className="cdm-list">
                            <li>Automated edit checks and manual data review listings.</li>
                            <li>Query generation, tracking, and resolution lifecycle management.</li>
                            <li>Cross-form, cross-visit, and cross-dataset consistency validation.</li>
                            <li>SAE–AE reconciliation and coding with MedDRA/WHO-DD.</li>
                            <li>Periodic data review meetings and quality metrics.</li>
                        </ul>
                    </article>

                    <article className="cdm-card">
                        <h3>External &amp; Vendor Data Management</h3>
                        <ul className="cdm-list">
                            <li>Management of central lab, ECG, PK/PD, imaging, ePRO, and other external datasets.</li>
                            <li>Coordination with external vendors and systems.</li>
                            <li>Data Transfer Specification development and validation.</li>
                            <li>Automated import, reconciliation, and exception log review.</li>
                            <li>Version control and traceability for vendor data updates.</li>
                        </ul>
                    </article>

                    <article className="cdm-card">
                        <h3>Medical Coding &amp; Dictionary Management</h3>
                        <ul className="cdm-list">
                            <li>MedDRA and WHO Drug coding with version control.</li>
                            <li>Auto/manual hybrid coding and approval trails.</li>
                            <li>Up-versioning and recoding support.</li>
                            <li>Standardized dictionary management for submission consistency.</li>
                        </ul>
                    </article>

                    <article className="cdm-card">
                        <h3>Data Review, Reconciliation &amp; Lock</h3>
                        <ul className="cdm-list">
                            <li>Dataset review for completeness and accuracy.</li>
                            <li>SAE and external data reconciliation with audit-ready documentation.</li>
                            <li>Query closure tracking and lock readiness assessment.</li>
                            <li>Controlled database lock/unlock execution and final transfer logs.</li>
                            <li>Secure archival of raw, cleaned, and standardized datasets.</li>
                        </ul>
                    </article>

                    <article className="cdm-card">
                        <h3>Standards, Compliance &amp; Governance</h3>
                        <ul className="cdm-list">
                            <li>Compliance with CDISC, CDASH, SDTM, and regulatory expectations.</li>
                            <li>GCP, GxP, and 21 CFR Part 11-compliant operational framework.</li>
                            <li>SOP-driven lifecycle documentation and deviation handling.</li>
                            <li>Inspection readiness and CAPA management support.</li>
                            <li>Change control, version history, and traceability management.</li>
                        </ul>
                    </article>

                    <article className="cdm-card">
                        <h3>Advanced &amp; Value-Added Services</h3>
                        <ul className="cdm-list">
                            <li>Risk-Based Data Management integrated with RBM frameworks.</li>
                            <li>Metadata-driven validation and data quality automation.</li>
                            <li>Real-time dashboards for anomaly detection and decision support.</li>
                            <li>Legacy study migration and CDISC conversion.</li>
                            <li>Data anonymization, transparency package, and submission readiness.</li>
                            <li>Clinical data management staffing solutions.</li>
                        </ul>
                    </article>

                    <article className="cdm-card">
                        <h3>Collaborative Ecosystem Advantage</h3>
                        <ul className="cdm-list">
                            <li>Collaboration with EDC vendors, CRO partners, central laboratories, and statistical teams.</li>
                            <li>Centralized communication workflows across stakeholders.</li>
                            <li>Integration with sponsor or partner systems including EDC, CTMS, eTMF, and safety databases.</li>
                            <li>Transparent governance model for on-time, audit-ready deliverables.</li>
                        </ul>
                    </article>
                </div>
            </section>
        </div>
    );
}